import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import ProductCard from "@/components/ProductCard";
import { inr, resolveImage } from "@/lib/api";
import { ArrowRight, CheckCircle2, Sparkles } from "lucide-react";

const BANNER_STYLES = {
  navy: "bg-navy text-white",
  teal: "bg-teal text-white",
  dark: "bg-gradient-to-br from-slate-900 via-slate-800 to-teal-950 text-white",
  light: "bg-white text-navy border-y border-slate-200",
};

function Hero({ d }) {
  return (
    <section className="relative overflow-hidden bg-navy text-white wbg-grid-bg" data-testid="section-hero">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 grid lg:grid-cols-2 gap-10 items-center">
        <div className="animate-fade-up">
          {d.eyebrow && <span className="eyebrow text-teal-500">{d.eyebrow}</span>}
          <h1 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight tracking-tight">{d.headline}</h1>
          {d.subheadline && <p className="mt-5 text-slate-300 text-base sm:text-lg leading-relaxed max-w-xl">{d.subheadline}</p>}
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            {d.primary_cta && (
              <Button asChild size="lg" className="bg-teal hover:bg-teal-500 text-white gap-2" data-testid="hero-primary-cta">
                <Link to={d.primary_cta_link || "/shop"}>{d.primary_cta} <ArrowRight className="w-4 h-4" /></Link>
              </Button>
            )}
            {d.secondary_cta && (
              <Button asChild size="lg" variant="outline" className="border-white/30 text-white hover:bg-white hover:text-navy bg-transparent" data-testid="hero-secondary-cta">
                <Link to={d.secondary_cta_link || "/shop"}>{d.secondary_cta}</Link>
              </Button>
            )}
          </div>
        </div>
        <div className="relative animate-fade-up">
          {d.image && <img src={resolveImage(d.image)} alt={d.headline || ""} className="rounded-2xl border border-white/10 shadow-2xl w-full object-cover aspect-[4/3]" />}
        </div>
      </div>
    </section>
  );
}

function RichPoints({ d }) {
  const bad = d.variant === "problem";
  return (
    <section className={bad ? "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" : "bg-white border-y border-slate-200"} data-testid="section-rich">
      <div className={bad ? "" : "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16"}>
        <div className={bad ? "grid lg:grid-cols-2 gap-10 items-center" : "text-center max-w-2xl mx-auto"}>
          <div>
            {d.eyebrow && <span className="eyebrow">{d.eyebrow}</span>}
            <h2 className="mt-3 text-2xl sm:text-3xl font-bold text-navy">{d.title}</h2>
          </div>
          {bad && (
            <ul className="space-y-3">
              {(d.points || []).map((p, i) => (
                <li key={i} className="flex gap-3 items-start bg-white border border-slate-200 rounded-lg p-4">
                  <span className="w-6 h-6 rounded-full bg-red-50 text-red-500 flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">✕</span>
                  <span className="text-slate-700 text-sm">{p}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        {!bad && (
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {(d.points || []).map((p, i) => (
              <div key={i} className="bg-background border border-slate-200 rounded-xl p-5">
                <CheckCircle2 className="w-7 h-7 text-teal mb-3" />
                <p className="text-slate-700 text-sm leading-relaxed">{p}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function FeatureGrid({ d }) {
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="section-features">
      <div className="text-center max-w-2xl mx-auto">
        {d.eyebrow && <span className="eyebrow">{d.eyebrow}</span>}
        <h2 className="mt-3 text-2xl sm:text-3xl font-bold text-navy">{d.title}</h2>
        {d.subtitle && <p className="mt-3 text-slate-500">{d.subtitle}</p>}
      </div>
      <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        {(d.items || []).map((item, i) => (
          <div key={i} className="bg-white border border-slate-200 rounded-xl p-5 hover:border-teal-500/50 hover:shadow-md transition-all">
            <span className="font-mono text-xs text-teal font-semibold">{String(i + 1).padStart(2, "0")}</span>
            <h3 className="font-heading font-semibold text-navy mt-1">{item.title}</h3>
            <p className="text-sm text-slate-500 mt-2 leading-relaxed">{item.desc}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function ProductGrid({ d }) {
  const products = d._products || [];
  return (
    <section className="bg-white border-y border-slate-200" data-testid="section-product-grid">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="flex items-end justify-between mb-8">
          <div>
            {d.eyebrow && <span className="eyebrow">{d.eyebrow}</span>}
            <h2 className="mt-2 text-2xl sm:text-3xl font-bold text-navy">{d.title}</h2>
          </div>
          <Button asChild variant="ghost" className="text-teal gap-1"><Link to={d.cta_link || "/shop"}>View all <ArrowRight className="w-4 h-4" /></Link></Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {products.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      </div>
    </section>
  );
}

function Promo({ d }) {
  const p = d._product;
  const img = resolveImage(d.image) || p?.cover_image;
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="section-promo">
      <div className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-teal-950 text-white border border-teal-500/30 rounded-2xl p-6 sm:p-10 shadow-xl grid lg:grid-cols-12 gap-8 items-center">
        <div className="lg:col-span-7">
          {d.badge && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-amber-500/15 text-amber-300 border border-amber-500/30">
              <Sparkles className="w-3 h-3" /> {d.eyebrow || d.badge}
            </span>
          )}
          <h2 className="mt-4 text-2xl sm:text-3xl lg:text-4xl font-extrabold">{d.heading || p?.name}</h2>
          {(d.text || p?.short_description) && <p className="mt-3 text-slate-300 max-w-lg">{d.text || p?.short_description}</p>}
          {p && (
            <div className="mt-6 flex items-baseline gap-3">
              <span className="text-4xl font-extrabold">{inr(p.price)}</span>
              {p.compare_at && <span className="text-lg text-slate-400 line-through">{inr(p.compare_at)}</span>}
              {p.compare_at && <span className="text-sm font-semibold text-teal-400">Save {inr(p.compare_at - p.price)}</span>}
            </div>
          )}
          {d.cta && (
            <Button asChild size="lg" className="mt-6 bg-teal hover:bg-teal-500 text-white gap-2">
              <Link to={d.cta_link || "/shop"}>{d.cta} <ArrowRight className="w-4 h-4" /></Link>
            </Button>
          )}
        </div>
        <div className="lg:col-span-5">
          {img && <img src={img} alt={d.heading || p?.name || ""} className="rounded-xl border border-white/10 w-full object-cover aspect-[4/3]" />}
        </div>
      </div>
    </section>
  );
}

function Banner({ d }) {
  const cls = BANNER_STYLES[d.style] || BANNER_STYLES.teal;
  return (
    <section className={cls} data-testid="section-banner">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 flex flex-col sm:flex-row items-center justify-between gap-6">
        <div className="flex items-center gap-5">
          {d.image && <img src={resolveImage(d.image)} alt="" className="w-20 h-20 rounded-xl object-cover border border-white/20" />}
          <div>
            {d.eyebrow && <span className="text-xs font-semibold uppercase tracking-wide opacity-80">{d.eyebrow}</span>}
            <h3 className="text-xl sm:text-2xl font-bold">{d.heading}</h3>
            {d.text && <p className="mt-1 text-sm opacity-90 max-w-xl">{d.text}</p>}
          </div>
        </div>
        {d.cta && (
          <Button asChild size="lg" className="bg-white text-navy hover:bg-slate-100 gap-2 shrink-0">
            <Link to={d.cta_link || "/shop"}>{d.cta} <ArrowRight className="w-4 h-4" /></Link>
          </Button>
        )}
      </div>
    </section>
  );
}

function Steps({ d }) {
  return (
    <section className="bg-white border-y border-slate-200" data-testid="section-steps">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center">
          {d.eyebrow && <span className="eyebrow">{d.eyebrow}</span>}
          <h2 className="mt-2 text-2xl sm:text-3xl font-bold text-navy">{d.title}</h2>
        </div>
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {(d.steps || []).map((s, i) => (
            <div key={i} className="relative">
              <div className="w-10 h-10 rounded-full bg-navy text-white flex items-center justify-center font-bold mb-3">{i + 1}</div>
              <h3 className="font-heading font-semibold text-navy">{s.title}</h3>
              <p className="text-sm text-slate-500 mt-1.5">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Tags({ d }) {
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="section-tags">
      <div className="text-center max-w-2xl mx-auto">
        {d.eyebrow && <span className="eyebrow">{d.eyebrow}</span>}
        <h2 className="mt-2 text-2xl sm:text-3xl font-bold text-navy">{d.title}</h2>
      </div>
      <div className="mt-8 flex flex-wrap justify-center gap-3">
        {(d.points || []).map((p, i) => (
          <span key={i} className="bg-white border border-slate-200 rounded-full px-5 py-2.5 text-sm font-medium text-slate-700">{p}</span>
        ))}
      </div>
    </section>
  );
}

function Faq({ d }) {
  return (
    <section className="bg-white border-y border-slate-200" data-testid="section-faq">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-8">
          {d.eyebrow && <span className="eyebrow">{d.eyebrow}</span>}
          <h2 className="mt-2 text-2xl sm:text-3xl font-bold text-navy">{d.title}</h2>
        </div>
        <Accordion type="single" collapsible className="w-full">
          {(d.items || []).map((f, i) => (
            <AccordionItem key={i} value={`faq-${i}`}>
              <AccordionTrigger className="text-left font-medium text-navy">{f.q}</AccordionTrigger>
              <AccordionContent className="text-slate-600">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}

function Founder({ d }) {
  if (!d.name && !d.bio) return null;
  return (
    <section className="bg-white border-y border-slate-200" data-testid="founder-section">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-3 gap-8 items-center">
          <div className="md:col-span-1">
            <div className="aspect-square rounded-2xl bg-gradient-to-br from-navy to-teal-950 border border-teal-500/20 flex items-center justify-center overflow-hidden">
              {d.image
                ? <img src={resolveImage(d.image)} alt={d.name} className="w-full h-full object-cover" />
                : <span className="font-serif text-5xl text-teal-400">{(d.name || "").split(" ").map((x) => x[0]).join("").slice(0, 2)}</span>}
            </div>
          </div>
          <div className="md:col-span-2">
            {d.eyebrow && <span className="eyebrow">{d.eyebrow}</span>}
            <h2 className="mt-2 text-2xl sm:text-3xl font-bold text-navy">{d.title}</h2>
            <p className="mt-4 text-slate-600 leading-relaxed">{d.bio}</p>
            <div className="mt-4">
              <div className="font-heading font-semibold text-navy">{d.name}</div>
              <div className="text-sm text-teal">{d.role}</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Cta({ d }) {
  return (
    <section className="bg-navy text-white" data-testid="section-cta">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold">{d.title}</h2>
        {d.subtitle && <p className="mt-3 text-slate-300">{d.subtitle}</p>}
        {d.cta && (
          <Button asChild size="lg" className="mt-8 bg-teal hover:bg-teal-500 text-white gap-2">
            <Link to={d.cta_link || "/shop"}>{d.cta} <ArrowRight className="w-4 h-4" /></Link>
          </Button>
        )}
      </div>
    </section>
  );
}

const MAP = {
  hero: Hero, rich_points: RichPoints, feature_grid: FeatureGrid, product_grid: ProductGrid,
  promo: Promo, banner: Banner, steps: Steps, tags: Tags, faq: Faq, founder: Founder, cta: Cta,
};

export function Section({ section }) {
  const Comp = MAP[section.type];
  if (!Comp) return null;
  return <Comp d={section.data || {}} />;
}

export default function SectionRenderer({ sections }) {
  if (!sections?.length) return null;
  return (
    <>
      {sections.map((s) => <Section key={s.id} section={s} />)}
    </>
  );
}
